/**
 * The star's bounds, its ballot and the one piece of arithmetic that makes its gamble a decision.
 *
 * A star is the rarest thing on the map: a single glowing pickup that appears every few minutes,
 * **only a human may take it**, and taking it opens the room-wide vote from `votes.ts` — HEADS or
 * TAILS, ten seconds, everybody in the room offered it and nothing frozen. What the star adds to
 * that general mechanism is exactly two things: the question it asks, and what its taker is paid.
 *
 * Its own file rather than a block in `simulation.ts`, on `votes.ts`' precedent one card back: the
 * numbers here are a *mechanic's* — a payout multiplier, a ballot, a rate — rather than a value
 * the growth curve or the wire was tuned against, and the entity's physical constants read better
 * beside the rule they exist for than beside PELLET_MASS_MAX. Everything it needs from the
 * simulation is imported, so there is still one definition of a spawn margin and one of a tick.
 *
 * **THE COIN IS NOT FLIPPED AND THERE IS NO COIN.** Nothing here draws, and nothing in the star's
 * resolution draws either: which side "wins" is decided by the room's own majority, so the star's
 * taker is betting on *people* rather than on a generator. The only draw the feature makes at all
 * is where and when a star appears, and that comes off `powerUpRng` because it happens because
 * TIME PASSED — see `RoomSim.stepStarField`.
 */

import type { VoteOption } from '../types/index.js';
import {
  POWER_UP_RADIUS,
  SPAWN_EDGE_MARGIN,
  TICK_RATE_HZ,
  UPDATE_INTERVAL_TICKS,
} from './simulation.js';

/**
 * How big a star is drawn and how far its pickup reaches, in world units.
 *
 * Bigger than an orb (POWER_UP_RADIUS, 22), which is the visual grammar the field already has:
 * a pellet is a dot, a shard is bigger, an orb is bigger again, and the thing that is bigger than
 * all of them is the one that appears once in several minutes. It is also what lets a star read
 * on the minimap at a glance, which is most of what makes crossing the arena for one worth doing.
 *
 * A fixture's size rather than state, like SPIKE_RADIUS, POWER_UP_RADIUS and PRIZE_SHARD_RADIUS,
 * so a `StarSnapshot` stays an id and a place.
 */
export const STAR_RADIUS = POWER_UP_RADIUS + 6;

/**
 * Mean seconds the field waits, **once it is empty**, before the next star appears. **Three
 * minutes.**
 *
 * **THIS IS AN OCCUPANCY PROBLEM AND NOT A RATE ONE, AND THAT IS THE TRAP THE NEXT PERSON TUNING
 * IT WILL FALL INTO.** `RoomSim.stepStarField` returns before the draw while a star is on the
 * field (`stars.size >= MAX_STARS_PER_ROOM`), so this is not a Poisson rate running concurrently
 * with the star that is already out there — it is the wait that starts when the field EMPTIES.
 * The two figures worth reading, writing `M` for this constant and `L` for
 * STAR_LIFETIME_SECONDS:
 *
 *   - **share of time a star is present** = `L / (L + M)`
 *   - **seconds between one star appearing and the next, if nobody takes them** = `L + M`
 *
 * Neither is `L / M`. At the pair this file now holds — `M` 180, `L` 120 — that is **a star
 * present 40% of the time and a star appearing every five minutes**, against the 22% and nine
 * minutes it was before. A pickup shortens the cycle further, because a star that is taken empties
 * the field early and starts the next wait there and then; those two figures are therefore the
 * *idle* room's, and they are the worst case rather than the typical one. Note the second-order
 * effect while you are in here: under a cap of one, a LONGER lifetime SUPPRESSES the appearance
 * rate, so the two numbers are not independent knobs.
 *
 * **WHAT THIS PAYS FOR AND WHY THE LIFETIME DID NOT PAY ANY OF IT.** The ask was for a star to
 * happen *more often*, which is a request about the second figure — how often there is a star
 * EVENT — so that is the one this pair is tuned against: 540 s to 300 s, nearly twice as many.
 * Cutting `L` to 90 buys 30 s more of that (270 s) and costs 7 points of occupancy, and it is the
 * wrong trade, because the lifetime is what makes a star *takeable*: a shorter one means more
 * stars expiring in a corner unseen, which lowers the rate of stars actually taken even as it
 * raises the rate of stars appearing. So the whole of the change is in `M`, and `L` is untouched.
 *
 * **THE OLD SEVEN MINUTES WAS PRICED AGAINST A COST THIS MECHANIC DOES NOT HAVE.** It justified
 * itself with "a star stops the whole room to ask it a question", and that is simply not what
 * happens: the vote's single loudest decision is that **nothing freezes** — every player steers,
 * eats, splits and duels for the whole ten seconds, and `room-sim.test.ts` pins byte-identical
 * masses and cell counts with a star vote open and without one. A room that gets asked a question
 * it can ignore while it plays is not a room where the vote is the game, so the rarity was buying
 * nothing. What a star still is, is the biggest single thing on the field and the only pickup a
 * bot may not take, and three minutes leaves it plainly the rarest object in the room: six times
 * POWER_UP_MEAN_SPAWN_SECONDS, against the fourteen it was.
 *
 * MAX_STARS_PER_ROOM stays 1 through all of this, for the reason its own comment gives.
 */
export const STAR_MEAN_SPAWN_SECONDS = 3 * 60;

/**
 * The per-opportunity spawn probability, derived rather than written down — POWER_UP_SPAWN_CHANCE's
 * arithmetic exactly, and it is derived for its reason: spawning is evaluated on the `powerup`
 * tier's own divisor, so the *rate* has to stay fixed if that tier is ever re-tuned. At five
 * opportunities a second and a mean of 180 s that is one in 900.
 */
export const STAR_SPAWN_OPPORTUNITIES_PER_SECOND = TICK_RATE_HZ / UPDATE_INTERVAL_TICKS.powerup;
export const STAR_SPAWN_CHANCE =
  1 / (STAR_MEAN_SPAWN_SECONDS * STAR_SPAWN_OPPORTUNITIES_PER_SECOND);

/**
 * How long a star nobody took lies on the field — two minutes, twice an orb's, and **unchanged by
 * the card that made stars more frequent**.
 *
 * Longer than POWER_UP_LIFETIME_SECONDS because a star is far rarer and has to be *crossed the map
 * for*: a one-minute window would mean stars expiring in a corner having been seen by nobody, and
 * a mechanic most players never meet is a mechanic nobody learns. It is the number that decides
 * whether a star that appears is one anybody gets to take, which is why the frequency was bought
 * entirely out of STAR_MEAN_SPAWN_SECONDS instead — see that constant for the arithmetic, and for
 * the reason `L / M` is not the steady state here. Against `M` at three minutes this puts a star
 * on the field 40% of the time in a room where nobody takes one, and appearing every five minutes.
 *
 * It despawns rather than waiting forever for PRIZE_SHARD_LIFETIME_SECONDS' reason, one step
 * harder: a star that waited would eventually make one corner of the map worth crossing it for on
 * a spawn that happened ten minutes ago. Under a cap of one it is also what holds the appearance
 * rate down, since no draw is made while a star is out there.
 */
export const STAR_LIFETIME_SECONDS = 120;
export const STAR_LIFETIME_TICKS = STAR_LIFETIME_SECONDS * TICK_RATE_HZ;

/**
 * The most stars one room will hold at once. **One.**
 *
 * Not a backstop like MAX_POWER_UPS_PER_ROOM (4) but the actual rule, and it follows from the vote
 * rather than from the field: `RoomSim.openVote` refuses a second vote while one is open, so two
 * stars on the field would mean a second one whose pickup is silently refused — a pickup that
 * looks identical to the first and does nothing. One star means the thing you can see is always
 * the thing that works.
 */
export const MAX_STARS_PER_ROOM = 1;

/**
 * How far from a world edge a star is placed — POWER_UP_EDGE_MARGIN's value and its reason: a
 * pickup pressed into a corner is one only a player who is already there can reach, because a
 * cluster clamped against two walls cannot centre itself on it.
 */
export const STAR_EDGE_MARGIN = SPAWN_EDGE_MARGIN;

/**
 * What a star-taker who gambles and calls it right is paid: **three times their OWN current mass**,
 * capped at what the pot actually holds.
 *
 * **THIS IS THE ONE NUMBER IN THE FEATURE THAT MAKES THE DECISION A DECISION, AND IT IS SIZED OFF
 * THE TAKER RATHER THAN OFF A WINNER'S SHARE ON PURPOSE.** The star-taker has two options and they
 * have to be worth comparing:
 *
 *  (a) the auto-win — skip the guess, be counted in the winning majority, take one ordinary
 *      winner's share `S` of the pot, guaranteed; or
 *  (b) the gamble — call it, and take the payout if the room agrees with you or **die** if it does
 *      not.
 *
 * Write `M` for the taker's own mass and `S` for a winner's share of the pot. What the gamble
 * risks is always `M` — the whole run — so the only question is what it is risking `M` *for*.
 *
 *  - **Sized off the taker's own mass (`3M`), the trade is the same trade at every size and it is
 *    worth making.** Its expected value is `0.5 × 3M − 0.5 × M = +M`: a coin at three-to-one on a
 *    stake that is, by construction, exactly what the player is risking. A beginner holding 40 mass
 *    and a leader holding 4 000 are offered the identical decision in the identical shape, which is
 *    what makes it a decision made with the stomach rather than read off the scoreboard.
 *  - **Sized off a SHARE (`3S`), the prize is DECOUPLED from the stake, and that is what breaks
 *    it.** The gamble's expected value becomes `0.5 × 3S − 0.5 × M`, which does not mention the
 *    thing being risked at all: whenever `S < M / 3` — a pot is half of what the *minority* was
 *    carrying and is then split among every winner, so that is the ordinary case and overwhelmingly
 *    so for anybody who has grown — it is **negative**, and it is also worse than simply taking the
 *    guaranteed `S`. Option (b) is dominated, (a) is the only answer, and the best decision in the
 *    feature is a non-decision. In the rare rooms where it is *not* dominated — a crumb standing
 *    beside a huge pot — it would be free money rather than a gamble, which is the same failure the
 *    other way up.
 *
 * Three rather than two because `2M` is `0.5 × 2M − 0.5 × M = +0.5M`, positive but thin enough
 * that a cautious player is right to decline every time; and rather than four because the payout
 * is capped at the pot anyway (see below), so a larger multiplier mostly buys nothing and, where
 * it does not, buys a leader four times their own mass out of one press.
 *
 * **THE AUTO-WIN IS NOT ALWAYS THE WORSE OPTION AND IT IS NOT MEANT TO BE.** A big pot with few
 * winners makes `S` enormous, and taking the certain share is then plainly right; a small or a
 * one-sided pot makes it nearly nothing, and the gamble is the only thing on the table worth
 * having. That the answer *changes with the room* is the whole feature — a payout that always beat
 * the alternative would be a button rather than a choice.
 *
 * **IT IS CAPPED AT THE POT, AND ZERO-SUM IS NON-NEGOTIABLE.** The pot is bounded by what the
 * minority actually lost after the VOTE_LOSS_FLOOR_MASS floor, which in a small or a one-sided
 * room is far less than `3M` — so the payout is `min(3M, pot)` and the winners split whatever is
 * left. A payout that exceeded the pot would be mass printed out of nothing, which no mechanic in
 * this game does. MAX_PLAYER_MASS (30 000) clamps the top of it a second time, so a star cannot
 * end a room even when the pot is enormous.
 *
 * **AND THIS IS NOT "TAKE 50% FROM EVERYBODY", WHICH WAS CONSIDERED AND REFUSED.** In a
 * hundred-player room half of everybody is on the order of 25 000 mass, which puts one player on
 * the size ceiling from a single press with no counterplay whatever — the game over, decided by a
 * pickup somebody drove into.
 */
export const STAR_GAMBLE_PAYOUT_MULTIPLIER = 3;

/** The two sides of the star's call, and the ids a client answers with. */
export const STAR_HEADS_OPTION_ID = 'heads';
export const STAR_TAILS_OPTION_ID = 'tails';

/**
 * The question a star asks the room.
 *
 * It says "the room" out loud because that is the mechanic: there is no coin and nothing is
 * flipped — the side with the most answers wins, so what a star-taker is betting on is what the
 * people around them will say. A prompt that promised a coin toss would be describing a draw the
 * simulation deliberately never makes.
 */
export const STAR_VOTE_PROMPT = 'A star! Call it — the room decides.';

/**
 * The ballot, frozen at module scope and copied by `RoomSim.openVote` before it is stored.
 *
 * Two options rather than MAX_VOTE_OPTIONS, because a star is a *call*: three sides would make the
 * gamble a one-in-three and the auto-win the only sane answer again.
 */
export const STAR_VOTE_OPTIONS: readonly VoteOption[] = [
  { id: STAR_HEADS_OPTION_ID, label: 'HEADS' },
  { id: STAR_TAILS_OPTION_ID, label: 'TAILS' },
];
